import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

import { IconEnvelopeOpen } from '../../../common/icons/icon-envelope-open';
import { useTranslation } from '../../../i18n';
import type { AuthenticationResult } from '../../models/auth.model';
import { LegacyAuthModalState } from '../../models/auth.model';
import { AccountCreateForm } from '../account-create-form/account-create.component';
import { LoginForm } from '../login-form/login-form.component';
import { ResetPasswordForm } from '../reset-password-form/reset-password-form.component';

type Props = {
  state?: LegacyAuthModalState;
  returnToUrl?: string;
  terms?: string;
  allowedStates?: LegacyAuthModalState[];
  onClose?: (result: AuthenticationResult) => void;
};

export function LegacyAuthModal({
  state: initialState,
  returnToUrl,
  terms,
  allowedStates = [
    LegacyAuthModalState.CREATE_ACCOUNT,
    LegacyAuthModalState.LOGIN,
    LegacyAuthModalState.RESET_PASSWORD,
  ],
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<LegacyAuthModalState>(
    initialState || LegacyAuthModalState.LOGIN
  );
  const [resolvedReturnTo, setResolvedReturnTo] = useState<string>(returnToUrl || '');
  const [email, setEmail] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);

  // Resolve returnToUrl on mount if not provided
  useEffect(() => {
    if (!returnToUrl) {
      try {
        setResolvedReturnTo(window.location.href);
      } catch {
        setResolvedReturnTo('/');
      }
    } else {
      setResolvedReturnTo(returnToUrl);
    }
  }, [returnToUrl]);

  const canCreate = useMemo(
    () => allowedStates.includes(LegacyAuthModalState.CREATE_ACCOUNT),
    [allowedStates]
  );

  const close = (response?: AuthenticationResult) => {
    if (response) {
      onClose?.(response);
    } else {
      onClose?.({
        success: false,
        termsAccepted: false,
        optInToMarketingEmails: false,
      });
    }
  };

  const handleShowVerifyEmail = (e: string) => {
    setEmail(e);
    setState(LegacyAuthModalState.VERIFY_EMAIL);
  };

  const resendLink = () => {
    if (email) {
      // this.commonFacade.dispatchSendCustAcctActivation(email);
    } else {
      console.error('no email for resending customer activation');
    }
  };

  return (
    <div class="single-legacy-auth-modal">
      {/* VERIFY EMAIL */}
      {state === LegacyAuthModalState.VERIFY_EMAIL && (
        <div class="single-modal-body single-verify-email">
          <div class="verify-email-container">
            <IconEnvelopeOpen width={60} height={60} />
            <h3>{t('auth.verificationRequired')}</h3>
            <p>{t('auth.verificationLinkSent')}</p>
            <button class="btn button" disabled={sending} onClick={resendLink}>
              {sending ? t('auth.sending') : t('auth.resendLink')}
            </button>
          </div>
        </div>
      )}

      {/* CREATE ACCOUNT */}
      {state === LegacyAuthModalState.CREATE_ACCOUNT && (
        <>
          <div class="single-modal-header">
            <h3 class="single-modal-title">{t('auth.createAccount')}</h3>
          </div>
          <div class="single-modal-body single-create-account">
            <AccountCreateForm
              returnToUrl={resolvedReturnTo}
              terms={terms}
              onClose={(res) => close(res)}
              onShowLogin={() => setState(LegacyAuthModalState.LOGIN)}
              onShowVerifyEmail={(e) => handleShowVerifyEmail(e)}
            />
          </div>
        </>
      )}

      {/* LOGIN */}
      {state === LegacyAuthModalState.LOGIN && (
        <>
          <div class="single-modal-header">
            <h3 class="single-modal-title">{t('auth.logIn')}</h3>
          </div>
          <div class="single-modal-body single-login">
            <LoginForm
              returnToUrl={resolvedReturnTo}
              terms={terms}
              showCreateAcct={canCreate}
              onClose={(res: AuthenticationResult | undefined) => close(res)}
              onShowAcctCreate={() => setState(LegacyAuthModalState.CREATE_ACCOUNT)}
              onShowReset={() => setState(LegacyAuthModalState.RESET_PASSWORD)}
            />
          </div>
        </>
      )}

      {/* RESET PASSWORD */}
      {state === LegacyAuthModalState.RESET_PASSWORD && (
        <>
          <div class="single-modal-header">
            <h3 class="single-modal-title">{t('auth.resetPassword')}</h3>
          </div>
          <div class="single-modal-body single-reset-password">
            <ResetPasswordForm
              returnToUrl={resolvedReturnTo}
              onShowLogin={() => setState(LegacyAuthModalState.LOGIN)}
            />
          </div>
        </>
      )}
    </div>
  );
}
